import { get } from 'lodash-es';

import { EMPTY_ARR } from '~/core/shared/constants';
import { formatFieldKeyOut } from '~/core/shared/fields/FieldUtils';
import {
  AnyDocument,
  Entity,
  Nullable,
  Option,
  WorkflowStepFormFieldBlock,
} from '~/core/shared/types';
import { getDocumentTargetAndLocationForPath } from '~/core/shared/utils';
import {
  applyTransformFuncs,
  capitalizeFirstLetter,
} from '~/core/shared/utils/general';
/**
 * Gets the default values for a set of form fields based on the provided document path and root document.
 *
 * @todo this currently does NOT support "groups" when block.componentProps?.isMulti is true, and when a document is found
 */
export const getDefaultValuesForFormFields = (
  formFieldBlocks: WorkflowStepFormFieldBlock[],
  rootDocument?: AnyDocument,
  documentPath?: string[],
): Entity => {
  const [document] = (
    documentPath
      ? getDocumentTargetAndLocationForPath(rootDocument, documentPath)
      : [null]
  ) as [Nullable<Document>];

  const defaultValues: Record<string, unknown> = {
    ...formFieldBlocks.reduce((defaultValuesAcc, block) => {
      let value = document
        ? get(document, formatFieldKeyOut(block.name))
        : undefined;

      // Handle special cases
      if (block.componentType === 'file-upload') {
        const blockMetadataType = block.componentProps?.metaData?.type;
        // TODO: this feels weird it uses the root document instead of the
        // document at the path, but this was the old behaviour so keeping for now
        value = (rootDocument?.docs || [])
          .filter(
            (doc: AnyDocument) =>
              !!doc.file &&
              (blockMetadataType ? blockMetadataType === doc.type : true),
          )
          .map(({ filename, id }: AnyDocument) => ({
            filename,
            id,
          }));
      } else if (block.componentProps?.isMulti) {
        if (document) {
          // TODO: this does not support groups
          const options = (block.componentProps?.options ??
            EMPTY_ARR) as Option<string>[];
          value = options
            .map((option): Nullable<string> => {
              const documentValue = get(
                document,
                `${block.name}${capitalizeFirstLetter(option.value)}`,
              );
              if (documentValue === true) {
                return option.value;
              }

              return null;
            })
            .filter(Boolean);
        } else {
          value = EMPTY_ARR;
        }
      }

      // Apply transform funcs
      if (block.transformFuncsIn && value !== undefined) {
        value = applyTransformFuncs(value, block.transformFuncsIn);
      } else if (value === undefined && 'defaultValue' in block) {
        value = block.defaultValue;
      }

      if (value === null) {
        value = undefined;
      }

      // Cast back to string
      if (
        value !== undefined &&
        typeof value !== 'string' &&
        !Array.isArray(value)
      ) {
        value = String(value);
      }

      return {
        ...defaultValuesAcc,
        [block.name]: value,
      };
    }, {}),
  };

  return defaultValues;
};
